import React, { useRef, useState, useEffect } from 'react';
import { Box, Text, HStack, VStack, Image, Button } from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import BackButton from '../components/BackButton';
import { useConfig } from '../contexts/ConfigContext';
import { getCachedEventApplications, sendLog, sendError, updateApplicationStatusInCache } from '../utils';
import { APPLICATION_STATUS, ApplicationStatusType, API_ROUTES, buildRoute, ROUTES } from '../constants';
import axios from 'axios';


const BusinessInfluencersListPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ApplicationStatusType | 'statistics'>('statistics');
  const [influencers, setInfluencers] = useState<any[]>([]);
  const [viewCount, setViewCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { config } = useConfig();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const categoryScrollRef = useRef<HTMLDivElement>(null);
  const categoryButtonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  
  // Get event details from location state
  const eventId = location.state?.eventId || '';
  const eventName = location.state?.eventName || '';
  
  // Handle return tab state
  useEffect(() => {
    if (location.state?.returnToTab) {
      setActiveTab(location.state.returnToTab);
    }
  }, [location.state?.returnToTab]);

  // Drag-to-scroll handlers
  const handleCategoryClick = (status: ApplicationStatusType | 'statistics') => {
    if (isDraggingRef.current) return;
    setActiveTab(status);
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!categoryScrollRef.current) return;
    isDraggingRef.current = true;
    const rect = categoryScrollRef.current.getBoundingClientRect();
    startXRef.current = e.pageX - rect.left;
    scrollLeftRef.current = categoryScrollRef.current.scrollLeft;
    categoryScrollRef.current.style.cursor = 'grabbing';
    categoryScrollRef.current.style.userSelect = 'none';
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current || !categoryScrollRef.current) return;
    e.preventDefault();
    const rect = categoryScrollRef.current.getBoundingClientRect();
    const pointerX = e.pageX - rect.left;
    const walk = (pointerX - startXRef.current) * 2;
    categoryScrollRef.current.scrollLeft = scrollLeftRef.current - walk;
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
    if (categoryScrollRef.current) {
      categoryScrollRef.current.style.cursor = 'grab';
      categoryScrollRef.current.style.userSelect = 'auto';
    }
  };

  const handleMouseLeave = () => {
    isDraggingRef.current = false;
    if (categoryScrollRef.current) {
      categoryScrollRef.current.style.cursor = 'grab';
      categoryScrollRef.current.style.userSelect = 'auto';
    }
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!categoryScrollRef.current) return;
    isDraggingRef.current = true;
    const rect = categoryScrollRef.current.getBoundingClientRect();
    startXRef.current = e.touches[0].pageX - rect.left;
    scrollLeftRef.current = categoryScrollRef.current.scrollLeft;
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current || !categoryScrollRef.current) return;
    const rect = categoryScrollRef.current.getBoundingClientRect();
    const pointerX = e.touches[0].pageX - rect.left;
    const walk = (pointerX - startXRef.current) * 2;
    categoryScrollRef.current.scrollLeft = scrollLeftRef.current - walk;
  };

  const handleTouchEnd = () => {
    isDraggingRef.current = false;
  };

  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!isDraggingRef.current || !categoryScrollRef.current) return;
      const rect = categoryScrollRef.current.getBoundingClientRect();
      const pointerX = e.pageX - rect.left;
      const walk = (pointerX - startXRef.current) * 2;
      categoryScrollRef.current.scrollLeft = scrollLeftRef.current - walk;
    };

    const handleGlobalMouseUp = () => {
      if (isDraggingRef.current) {
        isDraggingRef.current = false;
        if (categoryScrollRef.current) {
          categoryScrollRef.current.style.cursor = 'grab';
          categoryScrollRef.current.style.userSelect = 'auto';
        }
      }
    };

    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, []);

  // Fetch applications and influencer details
  useEffect(() => {
    const fetchApplicationsAndInfluencers = async () => {
      if (!config?.apiUrl || !eventId) {
        return;
      }

      setIsLoading(true);
      try {
        sendLog('BusinessInfluencersPageFetchAttempt', {
          eventId,
          eventName
        });

        // Fetch applications from cache or API (now includes merged influencer data)
        const applicationsData = await getCachedEventApplications(config.apiUrl, eventId);

        // Map applications with merged influencer data to display format
        const influencersData = applicationsData.map((app: any) => {
          const inf = app.influencer;
          
          // Handle case where influencer data might be missing
          if (!inf) {
            return {
              id: app.influencerId,
              applicationId: app.id,
              name: 'Profile unavailable',
              country: 'Unknown',
              completedEvents: 0,
              image: '',
              status: app.status,
              instagram: '',
              tiktok: '',
              youtube: '',
              birthday: '',
              allergies: ''
            };
          }

          // Extract influencer data from merged application
          return {
            id: app.influencerId,
            applicationId: app.id,
            name: `${inf.data?.firstName || ''} ${inf.data?.lastName || ''}`.trim() || 'Unknown',
            country: inf.city && inf.country 
              ? `${inf.city.charAt(0).toUpperCase() + inf.city.slice(1)}, ${inf.country.charAt(0).toUpperCase() + inf.country.slice(1)}`
              : 'Unknown',
            completedEvents: 0, // TODO: Add this to backend response if tracked
            image: inf.data?.photos?.[0] || '',
            status: app.status,
            instagram: inf.data?.social?.instagram || inf.data?.instagram || '',
            tiktok: inf.data?.social?.tiktok || inf.data?.tiktok || '',
            youtube: inf.data?.social?.youtube || inf.data?.youtube || '',
            birthday: inf.data?.birthday || '',
            allergies: inf.data?.allergies || ''
          };
        });
        
        setInfluencers(influencersData);

        sendLog('BusinessInfluencersPageLoadSuccess', {
          eventId,
          applicationsCount: applicationsData.length,
          influencersLoaded: influencersData.length
        });

        // Fetch event to get view count from viewedByInfluencers
        try {
          const eventResponse = await axios.get(`${config.apiUrl}${buildRoute(API_ROUTES.EVENTS_BY_ID, { id: eventId })}`);
          const event = eventResponse.data;
          setViewCount((event?.data?.viewedByInfluencers?.length) || 0);
        } catch (viewErr) {
          console.warn('Failed to fetch event for view count:', viewErr);
        }
      } catch (error: any) {
        const errorToLog = error instanceof Error 
          ? error 
          : new Error(error.message || 'Failed to fetch applications');
        
        sendError(errorToLog, {
          context: 'BusinessInfluencersListPage.fetchApplications',
          eventId,
          status: error.response?.status
        });
        
        setInfluencers([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchApplicationsAndInfluencers();
  }, [config?.apiUrl, eventId, eventName]);

  const handleApprove = async (influencerId: number) => {
    const influencer = influencers.find(inf => inf.id === influencerId);
    if (!influencer || !config?.apiUrl || !eventId) {
      return;
    }

    try {
      sendLog('BusinessApproveInfluencerFromList', {
        applicationId: influencer.applicationId,
        eventId,
        influencerId
      });

      // Update application status via API
      await axios.put(`${config.apiUrl}${buildRoute(API_ROUTES.APPLICATIONS_BY_ID, { id: influencer.applicationId })}`, {
        status: APPLICATION_STATUS.APPROVED
      });

      // Update the cache with new status
      updateApplicationStatusInCache(eventId, influencer.applicationId, APPLICATION_STATUS.APPROVED);

      // Update local state
      setInfluencers(prevInfluencers =>
        prevInfluencers.map(inf =>
          inf.id === influencerId
            ? { ...inf, status: APPLICATION_STATUS.APPROVED }
            : inf
        )
      );

      sendLog('BusinessApproveInfluencerFromListSuccess', {
        applicationId: influencer.applicationId,
        eventId,
        influencerId
      });
    } catch (error: any) {
      const errorToLog = error instanceof Error 
        ? error 
        : new Error(error.message || 'Failed to approve influencer');
      
      sendError(errorToLog, {
        context: 'BusinessInfluencersListPage.handleApprove',
        applicationId: influencer.applicationId,
        eventId,
        status: error.response?.status
      });
    }
  };

  const handleReject = async (influencerId: number) => {
    const influencer = influencers.find(inf => inf.id === influencerId);
    if (!influencer || !config?.apiUrl || !eventId) {
      return;
    }

    try {
      sendLog('BusinessRejectInfluencerFromList', {
        applicationId: influencer.applicationId,
        eventId,
        influencerId
      });

      // Update application status via API
      await axios.put(`${config.apiUrl}${buildRoute(API_ROUTES.APPLICATIONS_BY_ID, { id: influencer.applicationId })}`, {
        status: APPLICATION_STATUS.REJECTED
      });

      // Update the cache with new status
      updateApplicationStatusInCache(eventId, influencer.applicationId, APPLICATION_STATUS.REJECTED);

      // Update local state
      setInfluencers(prevInfluencers =>
        prevInfluencers.map(inf =>
          inf.id === influencerId
            ? { ...inf, status: APPLICATION_STATUS.REJECTED }
            : inf
        )
      );

      sendLog('BusinessRejectInfluencerFromListSuccess', {
        applicationId: influencer.applicationId,
        eventId,
        influencerId
      });
    } catch (error: any) {
      const errorToLog = error instanceof Error 
        ? error 
        : new Error(error.message || 'Failed to reject influencer');
      
      sendError(errorToLog, {
        context: 'BusinessInfluencersListPage.handleReject',
        applicationId: influencer.applicationId,
        eventId,
        status: error.response?.status
      });
    }
  };

  const handleBack = () => {
    console.log('Back button clicked, navigating to business home');
    navigate(ROUTES.BUSINESS_HOME);
  };

  const handleInfluencerClick = (influencer: any) => {
    navigate(ROUTES.BUSINESS_INFLUENCER_DETAILS, {
      state: {
        influencerData: influencer,
        fromTab: activeTab,
        eventId: eventId,
        eventName: eventName
      }
    });
  };

  // Filter influencers by status based on active tab
  const filteredInfluencers = influencers.filter(influencer => {
    return influencer.status === activeTab;
  });

  return (
    <Box
      className="base-container-long"
      ref={scrollContainerRef}
      data-scroll-container="true"
    >

      {/* Navigation Bar */}
      <Box
        className='edit-profiles-top'
         marginBottom="60px"
      >
         <BackButton
          onClick={handleBack}
          borderColor="#C6D959"
          backgroundColor="rgba(198, 217, 89, 0.1)"
        />

        <Text
          className='title-text'
          marginLeft="24px"
        >
          Influencers
        </Text>
      </Box>
      {/* Event Name */}
      <Box
        className='simple-box'
        top="104px"
      >
        <Text className='subtitle-text' >
          {eventName}
        </Text>
      </Box>

      {/* Divider */}
      <Box className="navigation-bar-divider"/>

      {/* Tab Navigation */}
      {/* Tab Buttons - Carousel Style */}
      <Box
        className="button-category-box"
        ref={categoryScrollRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <HStack
          spacing="8px"
          width="max-content"
          paddingLeft="24px"
          paddingRight="24px"
          paddingBottom="20px"
        >
          {/* Statistics Tab */}
          <Button
            className="button-category"
            ref={(el: HTMLButtonElement | null) => {
              categoryButtonRefs.current['statistics'] = el;
            }}
            backgroundColor={activeTab === 'statistics' ? '#C6D959' : '#1D1D1D'}
            color={activeTab === 'statistics' ? '#120F1B' : '#90909A'}
            fontWeight={activeTab === 'statistics' ? '500' : '400'}
            _hover={{
              backgroundColor: activeTab === 'statistics' ? '#b8cc4a' : '#2A2A2A'
            }}
            onClick={() => handleCategoryClick('statistics')}
          >
            Statistics
          </Button>

          {/* Candidates Tab */}
          <Button
            className="button-category"
            ref={(el: HTMLButtonElement | null) => {
              categoryButtonRefs.current[APPLICATION_STATUS.PENDING] = el;
            }}
            backgroundColor={activeTab === APPLICATION_STATUS.PENDING ? '#C6D959' : '#1D1D1D'}
            color={activeTab === APPLICATION_STATUS.PENDING ? '#120F1B' : '#90909A'}
            fontWeight={activeTab === APPLICATION_STATUS.PENDING ? '500' : '400'}
            _hover={{
              backgroundColor: activeTab === APPLICATION_STATUS.PENDING ? '#b8cc4a' : '#2A2A2A'
            }}
            onClick={() => handleCategoryClick(APPLICATION_STATUS.PENDING)}
          >
            Candidates
          </Button>

          {/* Approved Tab */}
          <Button
            className="button-category"
            ref={(el: HTMLButtonElement | null) => {
              categoryButtonRefs.current[APPLICATION_STATUS.APPROVED] = el;
            }}
            backgroundColor={activeTab === APPLICATION_STATUS.APPROVED ? '#C6D959' : '#1D1D1D'}
            color={activeTab === APPLICATION_STATUS.APPROVED ? '#120F1B' : '#90909A'}
            fontWeight={activeTab === APPLICATION_STATUS.APPROVED ? '500' : '400'}
            _hover={{
              backgroundColor: activeTab === APPLICATION_STATUS.APPROVED ? '#b8cc4a' : '#2A2A2A'
            }}
            onClick={() => handleCategoryClick(APPLICATION_STATUS.APPROVED)}
          >
            Approved
          </Button>

          {/* Rejected Tab */}
          <Button
            className="button-category"
            ref={(el: HTMLButtonElement | null) => {
              categoryButtonRefs.current[APPLICATION_STATUS.REJECTED] = el;
            }}
            backgroundColor={activeTab === APPLICATION_STATUS.REJECTED ? '#C6D959' : '#1D1D1D'}
            color={activeTab === APPLICATION_STATUS.REJECTED ? '#120F1B' : '#90909A'}
            fontWeight={activeTab === APPLICATION_STATUS.REJECTED ? '500' : '400'}
            _hover={{
              backgroundColor: activeTab === APPLICATION_STATUS.REJECTED ? '#b8cc4a' : '#2A2A2A'
            }}
            onClick={() => handleCategoryClick(APPLICATION_STATUS.REJECTED)}
          >
            Rejected
          </Button>

          {/* Cancelled Tab */}
          <Button
            className="button-category"
            ref={(el: HTMLButtonElement | null) => {
              categoryButtonRefs.current[APPLICATION_STATUS.CANCELLED] = el;
            }}
            backgroundColor={activeTab === APPLICATION_STATUS.CANCELLED ? '#C6D959' : '#1D1D1D'}
            color={activeTab === APPLICATION_STATUS.CANCELLED ? '#120F1B' : '#90909A'}
            fontWeight={activeTab === APPLICATION_STATUS.CANCELLED ? '500' : '400'}
            _hover={{
              backgroundColor: activeTab === APPLICATION_STATUS.CANCELLED ? '#b8cc4a' : '#2A2A2A'
            }}
            onClick={() => handleCategoryClick(APPLICATION_STATUS.CANCELLED)}
          >
            Cancelled
          </Button>
        </HStack>
      </Box>

      {/* Content Area */}
      <Box
        position="absolute"
        top="221px" /* Match InfluencerHomePage */
        left="24px"
        right="24px"
        bottom="98px"
        overflowY="auto"
        overflowX="hidden"
        zIndex={2}
        sx={{
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none'
        }}
      >
        {activeTab === 'statistics' ? (
          /* Statistics View */
          <VStack spacing="16px" align="stretch">
            <Box
              padding="16px"
              backgroundColor="rgba(198, 217, 89, 0.1)"
              borderRadius="12px"
              border="1px solid #C6D959"
            >
              <Text className="static-text" color="#C6D959" fontSize="24px" fontWeight="500">
                {viewCount}
              </Text>
              <Text className="static-text" fontSize="14px" color="#90909A">
                Views
              </Text>
            </Box>
            <Box
              padding="16px"
              backgroundColor="rgba(198, 217, 89, 0.1)"
              borderRadius="12px"
              border="1px solid #C6D959"
            >
              <Text className="static-text" color="#C6D959" fontSize="24px" fontWeight="500">
                {influencers.length}
              </Text>
              <Text className="static-text" fontSize="14px" color="#90909A">
                Applications
              </Text>
            </Box>
            <Box
              padding="16px"
              backgroundColor="rgba(198, 217, 89, 0.1)"
              borderRadius="12px"
              border="1px solid #C6D959"
            >
              <Text className="static-text" color="#C6D959" fontSize="24px" fontWeight="500">
                {influencers.filter(i => i.status === APPLICATION_STATUS.APPROVED).length}
              </Text>
              <Text className="static-text" fontSize="14px" color="#90909A">
                Approved
              </Text>
            </Box>
            <Box
              padding="16px"
              backgroundColor="rgba(198, 217, 89, 0.1)"
              borderRadius="12px"
              border="1px solid #C6D959"
            >
              <Text className="static-text" color="#C6D959" fontSize="24px" fontWeight="500">
                {influencers.filter(i => i.status === APPLICATION_STATUS.REJECTED).length}
              </Text>
              <Text className="static-text" fontSize="14px" color="#90909A">
                Rejected
              </Text>
            </Box>
            <Box
              padding="16px"
              backgroundColor="rgba(198, 217, 89, 0.1)"
              borderRadius="12px"
              border="1px solid #C6D959"
            >
              <Text className="static-text" color="#C6D959" fontSize="24px" fontWeight="500">
                {influencers.filter(i => i.status === APPLICATION_STATUS.CANCELLED).length}
              </Text>
              <Text className="static-text" fontSize="14px" color="#90909A">
                Cancelled
              </Text>
            </Box>
          </VStack>
        ) : (
          /* Influencers List */
          <VStack spacing="16px" align="stretch">
            {isLoading ? (
              <Box textAlign="center" padding="40px">
                <Text className="static-text">Loading applications...</Text>
              </Box>
            ) : filteredInfluencers.length === 0 ? (
              <Box textAlign="center" padding="40px">
                <Text className="static-text">No {activeTab} found</Text>
              </Box>
            ) : (
              filteredInfluencers.map((influencer) => (
              <Box
              className="vstack-main-box"
              key={influencer.id}
              height={activeTab === APPLICATION_STATUS.PENDING ? "139px" : "96px"}
              onClick={() => handleInfluencerClick(influencer)}
            >
              {/* Influencer Image - Left side */}
              <Box
                position="absolute"
                top="0"
                left="0"
                width="96px"
                height={activeTab === APPLICATION_STATUS.PENDING ? "139px" : "96px"}
                borderRadius="20px 0px 0px 20px"
                overflow="hidden"
                flexShrink={0}
              >
                <Image
                  src={influencer.image}
                  alt={influencer.name}
                  width="100%"
                  height="100%"
                  objectFit="cover"
                />
              </Box>

              {/* Content Area - Right side */}
              <Box
                position="absolute"
                top="0"
                left="96px"
                right="0"
                height={activeTab === APPLICATION_STATUS.PENDING ? "139px" : "96px"}
                backgroundColor="#120F1B"
                borderRadius="0px 20px 20px 0px"
                padding="17px 0px 0px 11px"
                display="flex"
                flexDirection="column"
              >
                {/* Influencer Name */}
                <Text
                  className='static-text-white'
                  marginBottom="8px"
                  maxWidth="100%"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {influencer.name}
                </Text>

                {/* Country */}
                <Text
                  className='static-text'
                  marginBottom="4px"
                  maxWidth="100%"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  Country - {influencer.country}
                </Text>

                {/* Completed Events */}
                <Text
                  className='static-text'
                  marginBottom={activeTab === APPLICATION_STATUS.PENDING ? "16px" : "0px"}
                  maxWidth="100%"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  Completed events - {influencer.completedEvents}
                </Text>

                {/* Action Buttons - Only for candidates */}
                {activeTab === APPLICATION_STATUS.PENDING && (
                  <HStack spacing="5px" position="absolute" bottom="10px" left="11px">
                    {/* Reject Button */}
                    <Button
                      className='button-influencers-action'
                      backgroundColor="#F00C23"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReject(influencer.id);
                      }}
                      _hover={{
                        backgroundColor: "#d00a1e"
                      }}
                    >
                      Reject
                    </Button>

                    {/* Approve Button */}
                    <Button
                      className='button-influencers-action'
                      backgroundColor="#C6D959"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleApprove(influencer.id);
                      }}
                      _hover={{
                        backgroundColor: "#b8cc4a"
                      }}
                    >
                      Approve
                    </Button>
                  </HStack>
                )}
              </Box>
            </Box>
            ))
            )}
          </VStack>
        )}
      </Box>

      {/* Bottom Navigation Buttons */}
      <Box className='bottom-navigation-bar-box' >
        {/* Home Button */}
        <Button
          className='bottom-navigation-bar-button'
          onClick={() => navigate(ROUTES.BUSINESS_HOME)}
        >
          <svg width="18" height="20" viewBox="0 0 18 20" fill="none">
            <path
              d="M9 0L0 7V20H7V12H11V20H18V7L9 0Z"
              fill="#C6D959"
              stroke="none"
              strokeWidth="1.5"
            />
          </svg>
        </Button>

        {/* Add Button */}
        <Button
          className='bottom-navigation-bar-button'
          onClick={() => {
            // Navigate to create event page
            navigate(ROUTES.BUSINESS_EVENT_CREATION);
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M12 5V19M5 12H19"
              stroke="#90909A"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Button>

        {/* Profile Button */}
        <Button
          className='bottom-navigation-bar-button'
          onClick={() => navigate(ROUTES.BUSINESS_PROFILE)}
        >
          <svg width="16" height="18" viewBox="0 0 16 18" fill="none">
            <path
              d="M8 0C5.79 0 4 1.79 4 4C4 6.21 5.79 8 8 8C10.21 8 12 6.21 12 4C12 1.79 10.21 0 8 0ZM8 10C5.33 10 0 11.34 0 14V16C0 17.1 0.9 18 2 18H14C15.1 18 16 17.1 16 16V14C16 11.34 10.67 10 8 10Z"
              fill="none"
              stroke="#90909A"
              strokeWidth="1.5"
            />
          </svg>
        </Button>
      </Box>
    </Box>
  );
};

export default BusinessInfluencersListPage;
